// SKILLSMITH-OPPORTUNITIES in the in-memory API: the helper sales pipeline
// (`operator.opportunities.list|get|draft|send|stage`), the operator's helper quotes
// (`operator.helperOffers.list|create|status`) and the client's side of them on Billing
// (`billing.helperOffers.list|decide`), with the real routes' rules —
// - operator routes: platform super admins only (404 to anyone else; the twin's super admin is an
//   agency `admin`), granted companies only;
// - stages: dismissed from suggested|offered; reopened (suggested) from dismissed|declined;
//   accepted / declined from suggested|offered; added from accepted|offered|suggested; else 409.
//   `amount_minor` only with accepted / added;
// - the offer email: drafted while suggested or offered and unsent; Send needs a subject, a body
//   and a recipient, once, and moves a suggested opportunity to offered;
// - a quote: open → (client) building | declined; (operator) building → added, open → canceled,
//   each moving its opportunity along (accepted / declined / added / back to suggested).
// The demo companies have no opportunities: `fileOpportunity` (the
// `__companyosFakeSkillsmithOpportunity` hook) files one as a company's Skillsmith would.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, problem, validation } from "./fake";
import { type FakeCompany, uid } from "./fake-data";

type Opportunity = OperationOutput<"operator.opportunities.send">;
type Pipeline = OperationOutput<"operator.opportunities.list">;
type Detail = OperationOutput<"operator.opportunities.get">;
type Person = Detail["people"][number];
type Offer = OperationOutput<"operator.helperOffers.status">;
type Stage = Opportunity["stage"];
type Currency = Opportunity["currency"];
type Hub = Opportunity["company"];

/** What a test files: the system agent's create body, every field optional. */
export type FakeSkillsmithOpportunity = Partial<
  Omit<OperationInput<"operator.systemAgents.opportunities.create">["body"], "company_id">
> & {
  source?: Opportunity["source"];
  request_ref?: string;
  created_at?: string;
};

const STAGES = ["suggested", "offered", "accepted", "declined", "added", "dismissed"] as const;

/** Where each move may come from (the server's table). */
const FROM: Record<"suggested" | "dismissed" | "accepted" | "declined" | "added", Stage[]> = {
  dismissed: ["suggested", "offered"],
  suggested: ["dismissed", "declined"],
  accepted: ["suggested", "offered"],
  declined: ["suggested", "offered"],
  added: ["accepted", "offered", "suggested"],
};

const conflict = (detail: string) => problem(409, "conflict", "Conflict", detail);

type StoredOffer = { company_id: string; opportunity_id: string | null; offer: Offer };

export function createOpportunitiesFake(deps: {
  now: () => Date;
  companies: () => FakeCompany[];
  /** The company's people (offer recipients; owners and admins decide quotes). */
  people: (companyId: string) => Person[];
  /** The company's DEs (the DE an opportunity is about). */
  des: (companyId: string) => { membership_id: string; name: string }[];
  /** A quote reached the company: its owners and admins get an Inbox row. */
  notify: (companyId: string, offer: Offer, to: string[]) => void;
}) {
  let seq = 0;
  let invoiceSeq = 0;
  const opps: Omit<Opportunity, "helper_offer">[] = [];
  const offers: StoredOffer[] = [];

  const hub = (c: FakeCompany): Hub => ({ id: c.id, slug: c.slug, name: c.name });
  const companyOf = (id: string) => deps.companies().find((c) => c.id === id) ?? null;
  const stamp = () => deps.now().toISOString();

  /** The companies a super admin reads here (null: not a super admin, so 404). */
  function grantedOf(ctx: Ctx): Set<string> | null {
    const agency = ctx.me.agency;
    if (agency?.role !== "admin") return null;
    // only operator-level grants (as the API: a viewer grant does not admit the hub)
    return new Set(agency.grants.filter((g) => g.level === "operator").map((g) => g.company_id));
  }

  const offerOf = (oppId: string) =>
    offers.filter((o) => o.opportunity_id === oppId).at(-1)?.offer ?? null;

  const view = (o: Omit<Opportunity, "helper_offer">): Opportunity => ({
    ...o,
    helper_offer: offerOf(o.id),
  });

  function fileOpportunity(companyId: string, p: FakeSkillsmithOpportunity = {}): Opportunity {
    const c = companyOf(companyId);
    if (!c) throw new Error(`no company ${companyId}`);
    const des = deps.des(companyId);
    const de =
      des.find((d) => d.membership_id === p.de_membership_id) ??
      (p.de_membership_id ? { membership_id: p.de_membership_id, name: "DE" } : (des[0] ?? null));
    const suggested_name = p.suggested_name ?? "Interview scheduler";
    const kind = p.suggestion_kind ?? "helper";
    const deName = de?.name ?? "your DE";
    const o: Omit<Opportunity, "helper_offer"> = {
      id: uid(0x0bb0, ++seq),
      company: hub(c),
      source: p.source ?? "skillsmith",
      de,
      title: p.title ?? `${deName} keeps failing to book interviews`,
      problem:
        p.problem ??
        `${deName} retries calendar bookings by hand and the team corrects its invites.`,
      evidence_counts: p.evidence_counts ?? { run_failures: 7, corrections: 3 },
      evidence: p.evidence ?? [],
      suggestion_kind: kind,
      suggested_name,
      suggested_what: p.suggested_what ?? "Books interviews in the shared calendar.",
      expected_benefit: p.expected_benefit ?? "Fewer failed runs and no hand corrections.",
      confidence: p.confidence ?? "medium",
      stage: "suggested",
      request_ref: p.request_ref ?? null,
      offer: {
        subject: p.offer_draft?.subject ?? `A ${kind} for ${deName}: ${suggested_name}`,
        body:
          p.offer_draft?.body ??
          `Hi,\n\nWe noticed ${deName} could use a ${kind}: ${suggested_name}.\n\nReply and we will set it up.\n\nThe HTVA team`,
        to: [],
        edited_at: null,
        sent_at: null,
      },
      amount_minor: null,
      currency: c.reporting_currency as Currency,
      stage_changed_at: null,
      created_at: p.created_at ?? stamp(),
    };
    opps.push(o);
    return view(o);
  }

  /** The opportunity a super admin of a granted company acts on (null: 404). */
  function oppFor(ctx: Ctx) {
    const granted = grantedOf(ctx);
    if (!granted?.has(ctx.params.company_id as string)) return null;
    return (
      opps.find(
        (o) => o.id === ctx.params.opportunity_id && o.company.id === ctx.params.company_id,
      ) ?? null
    );
  }

  function move(o: Omit<Opportunity, "helper_offer">, stage: Stage) {
    o.stage = stage;
    o.stage_changed_at = stamp();
  }

  const linked = (s: StoredOffer) =>
    s.opportunity_id ? (opps.find((o) => o.id === s.opportunity_id) ?? null) : null;

  const handlers: Partial<Record<RouteId, Handler>> = {
    "operator.opportunities.list": (ctx) => {
      const granted = grantedOf(ctx);
      if (!granted) return notFound();
      const q = ctx.url.searchParams;
      const companyId = q.get("company_id");
      const stage = q.get("stage");
      const scoped = opps.filter(
        (o) => granted.has(o.company.id) && (!companyId || o.company.id === companyId),
      );
      const counts = Object.fromEntries(
        STAGES.map((s) => [s, scoped.filter((o) => o.stage === s).length]),
      ) as Pipeline["pipeline"]["counts"];
      const revenue = new Map<Currency, number>();
      for (const o of scoped) {
        if (o.stage !== "accepted" && o.stage !== "added") continue;
        const q = offerOf(o.id);
        const amount = o.amount_minor ?? (q ? q.one_off_minor + q.monthly_minor : 0);
        const cur = o.amount_minor !== null ? o.currency : (q?.currency ?? o.currency);
        revenue.set(cur, (revenue.get(cur) ?? 0) + amount);
      }
      const body: Pipeline = {
        pipeline: {
          counts,
          revenue: [...revenue].map(([currency, amount_minor]) => ({ currency, amount_minor })),
        },
        data: scoped
          .filter((o) => !stage || o.stage === stage)
          .sort((a, b) => b.created_at.localeCompare(a.created_at))
          .map(view),
        companies: deps
          .companies()
          .filter((c) => granted.has(c.id))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(hub),
      };
      return json(body);
    },

    "operator.opportunities.get": (ctx) => {
      const o = oppFor(ctx);
      if (!o) return notFound();
      const body: Detail = { opportunity: view(o), people: deps.people(o.company.id) };
      return json(body);
    },

    "operator.opportunities.draft": (ctx) => {
      const o = oppFor(ctx);
      if (!o) return notFound();
      const b = (ctx.body ?? {}) as { subject?: string; body?: string; to?: unknown };
      const subject = b.subject?.trim() ?? "";
      const text = b.body?.trim() ?? "";
      if (!subject || subject.length > 200) return validation("subject: 1 to 200 characters");
      if (!text || text.length > 5000) return validation("body: 1 to 5000 characters");
      if (!Array.isArray(b.to) || b.to.length < 1 || b.to.length > 20)
        return validation("to: 1 to 20 recipients");
      const people = new Set(deps.people(o.company.id).map((p) => p.membership_id));
      if (b.to.some((id) => typeof id !== "string" || !people.has(id)))
        return validation("to: the company's people only");
      if (o.offer.sent_at) return conflict("The offer was already sent.");
      if (o.stage !== "suggested" && o.stage !== "offered")
        return conflict(`The opportunity is ${o.stage}.`);
      o.offer = {
        ...o.offer,
        subject,
        body: text,
        to: [...new Set(b.to as string[])],
        edited_at: stamp(),
      };
      return json(view(o));
    },

    "operator.opportunities.send": (ctx) => {
      const o = oppFor(ctx);
      if (!o) return notFound();
      if (o.offer.sent_at) return conflict("The offer was already sent.");
      if (!o.offer.subject || !o.offer.body || o.offer.to.length === 0)
        return conflict("Draft the offer first.");
      if (o.stage !== "suggested" && o.stage !== "offered")
        return conflict(`The opportunity is ${o.stage}.`);
      o.offer = { ...o.offer, sent_at: stamp() };
      if (o.stage === "suggested") move(o, "offered");
      return json(view(o));
    },

    "operator.opportunities.stage": (ctx) => {
      const o = oppFor(ctx);
      if (!o) return notFound();
      const b = (ctx.body ?? {}) as { stage?: string; amount_minor?: unknown; currency?: string };
      const stage = b.stage as keyof typeof FROM | undefined;
      if (!stage || !(stage in FROM))
        return validation("stage: suggested, dismissed, accepted, declined or added");
      if (b.amount_minor !== undefined) {
        if (stage !== "accepted" && stage !== "added")
          return validation("amount_minor only with accepted or added");
        if (!Number.isInteger(b.amount_minor) || (b.amount_minor as number) < 0)
          return validation("amount_minor: a whole number of cents");
      }
      const live = offers.some(
        (s) =>
          s.opportunity_id === o.id && (s.offer.status === "open" || s.offer.status === "building"),
      );
      if (live) return conflict("Its quote decides the stage now; cancel the quote first.");
      if (!FROM[stage].includes(o.stage))
        return conflict(`An opportunity ${o.stage} cannot move to ${stage}.`);
      move(o, stage);
      if (b.amount_minor !== undefined) o.amount_minor = b.amount_minor as number;
      if (b.currency) o.currency = b.currency as Currency;
      return json(view(o));
    },

    "operator.helperOffers.list": (ctx) => {
      const granted = grantedOf(ctx);
      const id = ctx.params.company_id as string;
      if (!granted?.has(id) || !companyOf(id)) return notFound();
      return json({
        data: offers
          .filter((s) => s.company_id === id)
          .map((s) => s.offer)
          .reverse(),
      });
    },

    "operator.helperOffers.create": (ctx) => {
      const granted = grantedOf(ctx);
      const id = ctx.params.company_id as string;
      const c = companyOf(id);
      if (!granted?.has(id) || !c) return notFound();
      const b = (ctx.body ?? {}) as Partial<OperationInput<"operator.helperOffers.create">["body"]>;
      const name = b.helper_name?.trim() ?? "";
      if (!name || name.length > 120) return validation("helper_name: 1 to 120 characters");
      const oneOff = b.one_off_minor;
      const monthly = b.monthly_minor;
      if (!Number.isInteger(oneOff) || (oneOff as number) < 0)
        return validation("one_off_minor: a whole number of cents");
      if (!Number.isInteger(monthly) || (monthly as number) < 0)
        return validation("monthly_minor: a whole number of cents");
      if ((oneOff as number) + (monthly as number) <= 0)
        return validation("a quote has a one-off or a monthly price");
      const opp = b.opportunity_id
        ? opps.find((o) => o.id === b.opportunity_id && o.company.id === id)
        : null;
      if (b.opportunity_id && !opp) return notFound();
      if (opp) {
        const open = offers.some(
          (s) =>
            s.opportunity_id === opp.id &&
            (s.offer.status === "open" || s.offer.status === "building"),
        );
        if (open) return conflict("This opportunity already has an open quote.");
        if (opp.stage !== "suggested" && opp.stage !== "offered")
          return conflict(`The opportunity is ${opp.stage}.`);
      }
      const ref = b.request_ref ?? opp?.request_ref ?? null;
      if (
        ref &&
        offers.some(
          (s) =>
            s.company_id === id &&
            s.offer.request_ref === ref &&
            (s.offer.status === "open" || s.offer.status === "building"),
        )
      )
        return conflict("This request already has an open quote.");
      const deId = b.de_membership_id ?? opp?.de?.membership_id ?? null;
      const de = deId
        ? (deps.des(id).find((d) => d.membership_id === deId) ??
          (opp?.de?.membership_id === deId ? opp.de : null))
        : null;
      if (deId && !de) return validation("de_membership_id: not a DE of the company");
      const offer: Offer = {
        id: uid(0x0bb1, ++seq),
        de,
        helper_name: name,
        description: b.description?.trim() || null,
        one_off_minor: oneOff as number,
        monthly_minor: monthly as number,
        currency: (b.currency ?? "USD") as Currency,
        status: "open",
        request_ref: ref,
        decided_at: null,
        charge_status: "none",
        invoice: null,
        subscription_line: null,
        added_at: null,
        created_at: stamp(),
      };
      offers.push({ company_id: id, opportunity_id: opp?.id ?? null, offer });
      if (opp && opp.stage !== "offered") move(opp, "offered");
      deps.notify(
        id,
        offer,
        deps
          .people(id)
          .filter((p) => p.role === "owner" || p.role === "admin")
          .map((p) => p.membership_id),
      );
      return json(offer, 201);
    },

    "operator.helperOffers.status": (ctx) => {
      const granted = grantedOf(ctx);
      const id = ctx.params.company_id as string;
      if (!granted?.has(id)) return notFound();
      const s = offers.find((x) => x.company_id === id && x.offer.id === ctx.params.offer_id);
      if (!s) return notFound();
      const status = (ctx.body as { status?: string } | null)?.status;
      if (status !== "added" && status !== "canceled")
        return validation("status: added or canceled");
      const opp = linked(s);
      if (status === "added") {
        if (s.offer.status !== "building")
          return conflict(`The quote is ${s.offer.status}; only an approved one is added.`);
        s.offer = { ...s.offer, status: "added", added_at: stamp() };
        if (opp) move(opp, "added");
      } else {
        if (s.offer.status !== "open")
          return conflict(`The quote is ${s.offer.status}; only an open one is canceled.`);
        s.offer = { ...s.offer, status: "canceled" };
        if (opp) move(opp, opp.offer.sent_at ? "offered" : "suggested");
        s.opportunity_id = null; // unlinked: the opportunity is free for a new quote
      }
      return json(s.offer);
    },

    "billing.helperOffers.list": (ctx) => {
      const p = ctx.principal;
      if (!p?.manager) return notFound();
      return json({
        data: offers
          .filter((s) => s.company_id === p.company.id)
          .map((s) => s.offer)
          .reverse(),
        can_decide: p.roles.includes("owner") || p.roles.includes("admin"),
      });
    },

    "billing.helperOffers.decide": (ctx) => {
      const p = ctx.principal;
      if (!p?.manager) return notFound();
      const s = offers.find(
        (x) => x.company_id === p.company.id && x.offer.id === ctx.params.offer_id,
      );
      if (!s) return notFound();
      if (!p.roles.includes("owner") && !p.roles.includes("admin"))
        return problem(403, "forbidden", "Forbidden", "Only the company's owner or admin decides.");
      const decision = (ctx.body as { decision?: string } | null)?.decision;
      if (decision !== "approve" && decision !== "decline")
        return validation("decision: approve or decline");
      if (s.offer.status !== "open") return conflict(`The quote is ${s.offer.status}.`);
      const at = stamp();
      const opp = linked(s);
      if (decision === "approve") {
        const o = s.offer;
        s.offer = {
          ...o,
          status: "building",
          decided_at: at,
          charge_status: o.one_off_minor > 0 ? "recorded" : "none",
          invoice:
            o.one_off_minor > 0
              ? {
                  number: `HLP-${at.slice(0, 4)}-${String(++invoiceSeq).padStart(4, "0")}`,
                  amount_minor: o.one_off_minor,
                  at,
                }
              : null,
          subscription_line:
            o.monthly_minor > 0
              ? { label: `Helper: ${o.helper_name}`, monthly_minor: o.monthly_minor }
              : null,
        };
        if (opp) {
          move(opp, "accepted");
          if (opp.amount_minor === null) {
            opp.amount_minor = o.one_off_minor + o.monthly_minor;
            opp.currency = o.currency;
          }
        }
      } else {
        s.offer = { ...s.offer, status: "declined", decided_at: at };
        if (opp) move(opp, "declined");
      }
      return json(s.offer);
    },
  };

  return { handlers, fileOpportunity };
}
